import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchStocktakeById, updateStocktakeCountRpc, finalizeStocktakeRpc } from '@/services/stocktake';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useAuth } from '@/context/AuthContext';
import { Stocktake, StocktakeItem } from '@/types/models';
import {
  ClipboardCheck,
  Loader2,
  X,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

interface Props {
  stocktakeId: string;
  isOpen: boolean;
  onClose: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' });
}

export const StocktakeCountModal: React.FC<Props> = ({ stocktakeId, isOpen, onClose }) => {
  const { hasPermission } = useAuth();
  const isManagerOrOwner = hasPermission('purchases');
  const queryClient = useQueryClient();

  const [localCounts, setLocalCounts] = useState<Record<string, number>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [finalizeReason, setFinalizeReason] = useState('');
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [finalizeResult, setFinalizeResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data: stocktake,
    isLoading,
    error: loadError,
    refetch,
  } = useQuery<Stocktake>({
    queryKey: ['stocktake-detail', stocktakeId],
    queryFn: () => fetchStocktakeById(stocktakeId),
    enabled: isOpen && !!stocktakeId,
  });

  const saveMutation = useMutation({
    mutationFn: (item: StocktakeItem) =>
      updateStocktakeCountRpc({
        stocktake_id: stocktakeId,
        item_id: item.id,
        counted_quantity: localCounts[item.id] ?? (item.counted_quantity ?? 0),
        notes: null,
      }),
    onSuccess: () => void refetch(),
    onError: (err: unknown) => setError(parseDatabaseError(err)),
    onSettled: () => setSavingId(null),
  });

  const finalizeMutation = useMutation({
    mutationFn: () =>
      finalizeStocktakeRpc({ stocktake_id: stocktakeId, reason: finalizeReason.trim() }),
    onSuccess: (result) => {
      setFinalizeResult(result);
      void queryClient.invalidateQueries({ queryKey: ['stocktakes'] });
      void refetch();
      setShowFinalizeConfirm(false);
    },
    onError: (err: unknown) => setError(parseDatabaseError(err)),
  });

  const handleSaveCount = async (item: StocktakeItem) => {
    const val = localCounts[item.id];
    if (val === undefined) return; // unchanged
    setSavingId(item.id);
    setError(null);
    saveMutation.mutate(item);
  };

  if (!isOpen) return null;

  const isCompleted = stocktake?.status === 'completed';
  const items: StocktakeItem[] = (stocktake as any)?.items ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-brand-border">
          <div>
            <h2 className="text-sm font-bold text-brand-black flex items-center space-x-2">
              <ClipboardCheck className="w-4 h-4 text-brand-green" />
              <span>{stocktake?.title ?? 'Stocktake'}</span>
              <span
                className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${
                  stocktake?.status === 'completed'
                    ? 'bg-green-100 text-green-800'
                    : stocktake?.status === 'counting'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {stocktake?.status?.replace('_', ' ')}
              </span>
            </h2>
            {stocktake && (
              <p className="text-[11px] text-brand-muted mt-0.5">
                #{stocktake.stocktake_number} • Started: {formatDate(stocktake.created_at)}
                {stocktake.item_type !== 'all' && ` • ${stocktake.item_type} only`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-black">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 text-xs space-y-4">
          {error && <ErrorAlert message={error} />}
          {loadError && <ErrorAlert message={parseDatabaseError(loadError)} onRetry={() => refetch()} />}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <>
              {/* Finalize Result */}
              {finalizeResult && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-1 text-green-900">
                  <p className="font-bold text-sm flex items-center space-x-1">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Stocktake Finalized Successfully</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-center">
                    <div className="bg-white rounded border p-2">
                      <p className="font-bold text-brand-black text-base">{finalizeResult.total_lines_counted}</p>
                      <p className="text-brand-muted">Total Lines</p>
                    </div>
                    <div className="bg-white rounded border p-2">
                      <p className="font-bold text-green-700 text-base">{finalizeResult.matching_lines}</p>
                      <p className="text-brand-muted">Matching</p>
                    </div>
                    <div className="bg-white rounded border p-2">
                      <p className="font-bold text-red-700 text-base">{finalizeResult.shortage_lines}</p>
                      <p className="text-brand-muted">Shortages</p>
                    </div>
                    <div className="bg-white rounded border p-2">
                      <p className="font-bold text-amber-700 text-base">{finalizeResult.overage_lines}</p>
                      <p className="text-brand-muted">Overages</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Items Table */}
              <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
                <div className="p-3 bg-brand-bg border-b font-bold text-brand-black flex items-center justify-between">
                  <span>Stocktake Items ({items.length})</span>
                  {!isCompleted && (
                    <span className="text-[10px] text-brand-muted font-normal">
                      Enter counted quantities and save each row
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-center px-3 py-2">System Qty</th>
                        <th className="text-center px-3 py-2">Counted Qty</th>
                        <th className="text-center px-3 py-2">Variance</th>
                        {!isCompleted && <th className="px-3 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-border">
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-4 text-center text-brand-muted italic">
                            No items in this stocktake.
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => {
                          const currentVal = localCounts[item.id] ?? item.counted_quantity ?? '';
                          const sysQty = item.system_quantity ?? 0;
                          const countedQty = item.counted_quantity ?? null;
                          const variance = countedQty != null ? countedQty - sysQty : null;
                          const laptop = (item as any).laptop;
                          const accessory = (item as any).accessory;
                          const itemLabel = laptop
                            ? `${laptop.brand} ${laptop.model} (${laptop.stock_id})`
                            : accessory
                            ? `${accessory.name} [${accessory.sku}]`
                            : item.item_type;

                          return (
                            <tr key={item.id} className="hover:bg-brand-bg/40">
                              <td className="px-3 py-2 font-semibold text-brand-black">
                                <span>{itemLabel}</span>
                                <span className="ml-1 text-[10px] text-brand-muted uppercase font-normal">
                                  {item.item_type}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center font-bold">{sysQty}</td>
                              <td className="px-3 py-2 text-center">
                                {isCompleted ? (
                                  <span className="font-bold">{countedQty ?? '—'}</span>
                                ) : (
                                  <input
                                    type="number"
                                    min={0}
                                    value={currentVal}
                                    onChange={(e) =>
                                      setLocalCounts((prev) => ({
                                        ...prev,
                                        [item.id]: Number(e.target.value) || 0,
                                      }))
                                    }
                                    className="w-20 px-2 py-1 border border-brand-border rounded text-center font-bold focus:ring-1 focus:ring-brand-green"
                                  />
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {variance == null ? (
                                  <span className="text-brand-muted">—</span>
                                ) : variance === 0 ? (
                                  <span className="text-green-700 font-bold flex items-center justify-center space-x-0.5">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    <span>OK</span>
                                  </span>
                                ) : variance > 0 ? (
                                  <span className="text-amber-700 font-bold flex items-center justify-center space-x-0.5">
                                    <TrendingUp className="w-3.5 h-3.5" />
                                    <span>+{variance}</span>
                                  </span>
                                ) : (
                                  <span className="text-red-700 font-bold flex items-center justify-center space-x-0.5">
                                    <TrendingDown className="w-3.5 h-3.5" />
                                    <span>{variance}</span>
                                  </span>
                                )}
                              </td>
                              {!isCompleted && (
                                <td className="px-3 py-2">
                                  <button
                                    type="button"
                                    onClick={() => handleSaveCount(item)}
                                    disabled={localCounts[item.id] === undefined || savingId === item.id}
                                    className="px-2 py-1 bg-brand-green text-white font-semibold rounded disabled:opacity-40 text-[10px] hover:bg-brand-green-hover"
                                  >
                                    {savingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Finalize Section — Owner/Manager only, not already completed */}
              {isManagerOrOwner && !isCompleted && !finalizeResult && (
                <div className="border-t border-brand-border pt-4">
                  {showFinalizeConfirm ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                      <p className="font-bold text-amber-900 flex items-center space-x-1">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Confirm Finalization</span>
                      </p>
                      <p className="text-amber-800">
                        This will lock the stocktake and adjust stock levels for all variances. This cannot be undone.
                      </p>
                      <div>
                        <label className="block font-bold text-brand-black uppercase mb-1">
                          Reason for Finalization <span className="text-brand-red">*</span>
                        </label>
                        <input
                          type="text"
                          value={finalizeReason}
                          onChange={(e) => setFinalizeReason(e.target.value)}
                          placeholder="e.g. End of month count, auditor sign-off..."
                          className="w-full px-3 py-1.5 border border-brand-border rounded"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setShowFinalizeConfirm(false)}
                          className="px-3 py-1.5 border font-semibold rounded text-brand-muted hover:text-brand-black"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => finalizeMutation.mutate()}
                          disabled={finalizeMutation.isPending || !finalizeReason.trim()}
                          className="inline-flex items-center space-x-1 px-4 py-1.5 bg-amber-600 text-white font-bold rounded hover:bg-amber-700 disabled:opacity-50"
                        >
                          {finalizeMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          <span>Finalize Stocktake</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowFinalizeConfirm(true)}
                        className="inline-flex items-center space-x-1.5 px-4 py-2 bg-amber-600 text-white font-bold rounded hover:bg-amber-700"
                      >
                        <ClipboardCheck className="w-4 h-4" />
                        <span>Finalize Stocktake</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-brand-border">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
